import React, { useEffect } from 'react'
import {
  TagBeta,
  Box,
  Flex,
  ButtonOutline,
  ButtonPrimary,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Text,
  Switch,
  FormHelperText,
  Link
} from 'front-end/src/components'
import { t, Trans } from '@lingui/macro'
import { brandColors } from 'shared/basicPalette'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useModal } from '../../../app/hooks/useModal'
import { SMTPServerDetailsModal } from './SMTPServerDetailsModal'
import { notification } from '../../../components/common/notifications/Notifications'
import {
  useCustomSmtpQuery,
  usePatchCustomSmtpMutation,
  useSetEnabledCustomSmtpMutation,
  useVerifyCustomSenderEmailMutation
} from './CustomSMTP.codegen'
import { useHistory, useLocation } from 'react-router-dom'
import { omit } from 'lodash'
import {
  useEditableSendersQuery,
  useResendSenderMutation
} from '../AccountPage.codegen'
import Spinner from '../../../components/common/spinner/Spinner'
import { CustomSmtpDisableWarning } from './CustomSmtpDisableWarning'

const CustomSMTPSchema = yup.object().shape({
  defaultSenderName: yup.string().required(),
  defaultSenderAddress: yup.string().email().required()
})
export const customSMTPSenderTokenVerificationQueryParam =
  'customsmtpsendertoken'

export const CustomSMTP = () => {
  const location = useLocation()
  const history = useHistory()
  const { data, refetch, loading } = useCustomSmtpQuery()
  const [patchSMTP] = usePatchCustomSmtpMutation()
  const [setCustomSMTPEnabled] = useSetEnabledCustomSmtpMutation()

  const { open, currentModal } = useModal()
  const [resendSenderVerification] = useResendSenderMutation()
  const [verifyCustomSender] = useVerifyCustomSenderEmailMutation()
  const { refetch: refetchSenders } = useEditableSendersQuery()

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)

    const verify = async (token) => {
      await verifyCustomSender({
        variables: {
          token
        }
      })
      refetch()
      refetchSenders()
    }

    if (queryParams.has(customSMTPSenderTokenVerificationQueryParam)) {
      verify(queryParams.get(customSMTPSenderTokenVerificationQueryParam))
      queryParams.delete(customSMTPSenderTokenVerificationQueryParam)
      history.replace({
        search: queryParams.toString()
      })
    }
  }, [])

  const customSmtpConfigValidated =
    !!data?.admin?.organisation?.custom_smtp?.configuration?.validated_at

  const formik = useFormik({
    initialValues: {
      defaultSenderName:
        data?.admin?.organisation?.custom_smtp?.emailIdentity?.name,
      defaultSenderAddress:
        data?.admin?.organisation?.custom_smtp?.emailIdentity?.email
    },
    validationSchema: CustomSMTPSchema,
    onSubmit: (formData) => saveSendersInfo(formData),
    enableReinitialize: true
  })

  if (loading) {
    return <Spinner />
  }

  const resendSender = async () => {
    try {
      await resendSenderVerification({
        variables: {
          id: data?.admin?.organisation?.custom_smtp?.emailIdentity?.id!
        }
      })
      notification.info(t`Verification email sent`)
    } catch (err) {
      notification.error(t`Couldn't send verification email`)

      throw err
    }
  }

  const verifiedMessage = () => (
    <FormHelperText color={brandColors.green.mid}>
      <span className="icon icon-correct" />
      <Trans>Verified</Trans>
    </FormHelperText>
  )

  const notVerifiedMessage = () => (
    <FormHelperText color={brandColors.red.mid}>
      <span className="icon icon-incorrect" />
      <Trans>Verification email sent. Not yet verified.</Trans>
      <Link color={brandColors.grey.dark} ml="4px" onClick={resendSender}>
        <Trans>Resend Verification</Trans>
      </Link>
    </FormHelperText>
  )

  const saveSendersInfo = async (formData) => {
    if (formData) {
      const customSmtp = data?.admin?.organisation?.custom_smtp
      if (customSmtp) {
        await patchSMTP({
          variables: {
            input: {
              configuration: omit(customSmtp.configuration, [
                'validated_at',
                '__typename'
              ]),
              mailFrom: {
                ...customSmtp.emailIdentity,
                name: formData.defaultSenderName,
                email: formData.defaultSenderAddress
              }
            }
          }
        })
        refetch()
        notification.info(t`Changes saved`)
      }
    }
  }

  const isCustomSMTPActivated = data?.admin?.organisation?.custom_smtp?.enabled
  const emailIdentityVerifiedAt =
    data?.admin?.organisation?.custom_smtp?.emailIdentity?.verified_at
  return (
    <Box>
      {currentModal}
      <Heading size="md" display="flex" alignItems="center">
        <Trans>Custom SMTP</Trans>&nbsp;
        <TagBeta />
      </Heading>

      <Text color={brandColors.grey.mid} mt="12px">
        <Trans>
          Set a custom SMTP to divert the mail out of the platform to go through
          your designated server. You will require to set a system sender
          address that is used by Looop. Note: Changing SMTP will require you to
          verify again your custom senders.
        </Trans>
      </Text>

      <Flex mt="12px" align="center">
        <ButtonOutline
          onClick={() =>
            open(SMTPServerDetailsModal).then(() => {
              refetch()

              notification.info(t`Changes saved`)
            })
          }
        >
          <Trans>Edit SMTP server details</Trans>
        </ButtonOutline>
        {customSmtpConfigValidated && (
          <Box fontSize="20px" ml="16px;">
            <span className="icon icon-correct" />
          </Box>
        )}
      </Flex>

      <form onSubmit={formik.handleSubmit}>
        <HStack
          align="flex-start"
          opacity={isCustomSMTPActivated ? '0.5' : '1'}
          pointerEvents={isCustomSMTPActivated ? 'none' : 'auto'}
        >
          <FormControl>
            <FormLabel>
              <Trans>Sender name</Trans>
            </FormLabel>
            <Input
              disabled={!customSmtpConfigValidated}
              variant="filled"
              name="defaultSenderName"
              value={formik.values.defaultSenderName}
              onChange={formik.handleChange}
            />
          </FormControl>
          <FormControl>
            <FormLabel>
              <Trans>Sender address</Trans>
            </FormLabel>
            <HStack>
              <Input
                disabled={!customSmtpConfigValidated}
                variant="filled"
                name="defaultSenderAddress"
                placeholder="email@domain.co"
                value={formik.values.defaultSenderAddress}
                onChange={formik.handleChange}
              />
              {formik.values.defaultSenderAddress && customSmtpConfigValidated && (
                <ButtonPrimary
                  type="submit"
                  disabled={!formik.dirty || !formik.isValid}
                >
                  <Trans>Save</Trans>
                </ButtonPrimary>
              )}
            </HStack>
            <Box>
              {!emailIdentityVerifiedAt &&
                formik.values.defaultSenderAddress &&
                !formik.dirty &&
                notVerifiedMessage()}
              {!formik.dirty && emailIdentityVerifiedAt && verifiedMessage()}
            </Box>
          </FormControl>
        </HStack>
      </form>

      <FormControl mt="12px">
        <Flex justify="space-between" w={['100%', 'calc(50% - 4px)']}>
          <FormLabel htmlFor="activateCustomSMTP" mt="6px">
            <Trans>Activate Custom SMTP</Trans>
          </FormLabel>
          <Switch
            id="activateCustomSMTP"
            isDisabled={!(customSmtpConfigValidated && emailIdentityVerifiedAt)}
            isChecked={isCustomSMTPActivated}
            onChange={async () => {
              const customSMTP = data?.admin?.organisation?.custom_smtp
              if (customSMTP) {
                if (isCustomSMTPActivated) {
                  await open(CustomSmtpDisableWarning)
                }
                const { errors } = await setCustomSMTPEnabled({
                  variables: {
                    value: !isCustomSMTPActivated
                  }
                })
                await refetch()
                formik.resetForm()

                if (!errors) {
                  notification.info(t`Changes saved`)
                }
              }
            }}
          />
        </Flex>
      </FormControl>
    </Box>
  )
}
